import { Check, ChevronDown, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import Link from 'next/link';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import type { ReaderTone } from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';
import { cn } from '@/lib/utils';
import { useChapterMenuWidth } from './use-chapter-menu-width';

interface ChaptersDropdownProps {
  chapters: TChapterWithoutContent[];
  selectedChapterId: string;
  setSelectedChapterId: (chapterId: string) => void;
  workId?: string;
  workPathPrefix?: '/fanfics' | '/originals';
  isRating18?: boolean;
  tone?: ReaderTone;
  onOpenChange?: (open: boolean) => void;
}

export const ChaptersDropdown = ({
  chapters,
  selectedChapterId,
  setSelectedChapterId,
  workId,
  workPathPrefix = '/fanfics',
  isRating18 = false,
  tone = 'light',
  onOpenChange,
}: ChaptersDropdownProps) => {
  const { chapter, isOpen, handleOpenChange, onSelectChapter, triggerRef, probeRef, menuWidth } =
    useChapterMenuWidth({
      chapters,
      selectedChapterId,
      setSelectedChapterId,
      onOpenChange,
    });

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-tone={tone}
          className={cn(
            'flex min-w-0 flex-1 items-center justify-between gap-2 rounded-[14px] border border-[#d1d5db] bg-white px-3 py-2 text-left text-[#111827]',
            'sm:w-1/2 sm:max-w-[50%] sm:flex-[0_0_50%]',
            'data-[chapter-wide=true]:sm:w-auto data-[chapter-wide=true]:sm:max-w-none data-[chapter-wide=true]:sm:flex-[0_0_auto]',
            'max-sm:px-2.5 max-sm:py-1.5',
            'data-[tone=dark]:border-white/20 data-[tone=dark]:bg-[#111827] data-[tone=dark]:text-white',
            'contrast-more:border-2 contrast-more:border-current',
          )}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-left text-sm font-black uppercase leading-tight max-sm:text-xs">
              {chapter?.title}
            </span>
            <span className="mt-0.5 block truncate text-left text-[11px] text-[#898989] max-sm:text-[10px]">
              Глава {chapters.findIndex((item) => item.id === selectedChapterId) + 1} из{' '}
              {chapters.length}
            </span>
          </span>
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="flex shrink-0 items-center justify-center"
          >
            <ChevronDown className="size-5" aria-hidden="true" />
          </motion.span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-tone={tone}
        className={cn(
          'max-h-[400px] overflow-y-auto rounded-xl border p-2 scrollbar-thin-dropdown !z-[10001]',
          '!border-[#e5e7eb] !bg-white !text-[#111827] !opacity-100 !backdrop-blur-none',
          'data-[tone=dark]:!border-white/[0.12] data-[tone=dark]:!bg-[#111827] data-[tone=dark]:!text-white',
          menuWidth == null && 'w-[var(--radix-popover-trigger-width)]',
        )}
        style={menuWidth == null ? undefined : { width: menuWidth }}
        align="start"
      >
        <div className="grid gap-1">
          {chapters.map((item, index) => {
            const isSelected = item.id === selectedChapterId;
            const chapterUrl = workId ? `${workPathPrefix}/${workId}/read/${item.id}` : '#';
            const shouldIndexing = !isRating18 && item.shouldIndexing;
            const relAttribute = shouldIndexing ? undefined : 'nofollow';

            return (
              <Link
                key={item.id}
                href={chapterUrl}
                rel={relAttribute}
                className={cn(
                  'flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-transparent px-3 py-2 text-left transition-colors',
                  tone === 'dark' ? 'hover:bg-white/10' : 'hover:bg-[#F3F4F6]',
                  isSelected && (tone === 'dark' ? 'bg-white/10' : 'bg-[#F3F4F6]'),
                )}
                onClick={(event) => {
                  onSelectChapter(event, item.id);
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-black uppercase leading-tight">
                    {item.title}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#898989]">
                    Глава {index + 1} из {chapters.length}
                  </div>
                </div>
                {item.isLocked ? (
                  <Lock className="ml-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                ) : (
                  isSelected && <Check className="ml-2 h-4 w-4 flex-shrink-0 text-blue-600" />
                )}
              </Link>
            );
          })}
        </div>
      </PopoverContent>
      <div
        ref={probeRef}
        aria-hidden="true"
        className="pointer-events-none"
        style={{ position: 'fixed', left: -9999, top: 0, visibility: 'hidden' }}
      >
        {chapters.map((item, index) => (
          <div key={item.id} className="flex w-max items-center gap-2 px-3">
            <div>
              <div className="text-sm font-black uppercase leading-tight">{item.title}</div>
              <div className="mt-0.5 text-[11px]">
                Глава {index + 1} из {chapters.length}
              </div>
            </div>
            <span className="ml-2 inline-flex size-4 shrink-0" />
          </div>
        ))}
      </div>
    </Popover>
  );
};
